import React from "react";
import { ValidationErrors } from "src/errors";
import { AuthFormData, AuthFormDataKey } from "src/types";
import { TextInputBox, ValidationErrorMessage, FormLvlErrorMessage } from "src/components";
import { useNavigate } from "react-router-dom";

interface AuthRequestAccountCreationProps {
  formData: AuthFormData;
  validationErrors?: ValidationErrors<AuthFormDataKey>;
  formLvlErrorMessage: string;
  handleInputChange: (field: AuthFormDataKey, value: string) => void;
}

const AuthRequestAccountCreation: React.FC<AuthRequestAccountCreationProps> = ({
  formData,
  validationErrors,
  formLvlErrorMessage,
  handleInputChange
}) => {
  const navigate = useNavigate();

  const isFormValid = formData.name && formData.vendorName && formData.email && formData.reasonForAccountCreation;

  return (
    <div className="space-y-8">
      <div className="lg:hidden text-center">
        <div className="inline-flex items-center">
          <img src="/logo.png" alt="Healf Logo" className="w-20" />
        </div>
      </div>

      <div className="text-center">
        <h2 className="text-3xl font-bold text-secondary-900 mb-2">Request Account Creation</h2>
        <p className="text-secondary-600">Submit a request for a new account to access the platform</p>
      </div>

      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <TextInputBox
              id="name"
              label="Full Name"
              placeholder="Enter your full name"
              value={formData.name}
              onChange={(value: string) => handleInputChange("name", value)}
              type="text"
              required
              error={validationErrors?.getErrorMessageByFormDataKey("name")}
              className="md:w-[90%] w-full"
            />
          </div>

          <div>
            <TextInputBox
              id="vendorName"
              label="Vendor/Company Name"
              placeholder="Enter your company name"
              value={formData.vendorName}
              onChange={(value: string) => handleInputChange("vendorName", value)}
              type="text"
              required
              error={validationErrors?.getErrorMessageByFormDataKey("vendorName")}
            />
          </div>
        </div>

        <div>
          <TextInputBox
            id="email"
            label="Email Address"
            placeholder="Enter your business email"
            value={formData.email}
            onChange={(value: string) => handleInputChange("email", value)}
            type="email"
            required
            error={validationErrors?.getErrorMessageByFormDataKey("email")}
          />
        </div>

        <div>
          <label htmlFor="reasonForAccountCreation" className="block text-sm font-medium text-secondary-700 mb-2">
            Reason for Account Creation <span className="text-red-500">*</span>
          </label>
          <textarea
            id="reasonForAccountCreation"
            placeholder="Please provide a detailed reason for requesting account access (minimum 10 characters)"
            value={formData.reasonForAccountCreation}
            onChange={(e) => handleInputChange("reasonForAccountCreation", e.target.value)}
            rows={4}
            required
            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 resize-none ${
              validationErrors?.getErrorMessageByFormDataKey("reasonForAccountCreation")
                ? "border-red-300 bg-red-50"
                : "border-secondary-300 bg-white hover:border-secondary-400"
            }`}
            minLength={10}
            maxLength={500}
          />
          {validationErrors?.getErrorMessageByFormDataKey("reasonForAccountCreation") && (
            <ValidationErrorMessage
              message={validationErrors.getErrorMessageByFormDataKey("reasonForAccountCreation")}
            />
          )}
          <p className="text-xs text-secondary-500 mt-1">{formData.reasonForAccountCreation.length}/500 characters</p>
        </div>

        <FormLvlErrorMessage message={formLvlErrorMessage} />

        <div className="space-y-4">
          <button
            className="w-full bg-primary-600 hover:bg-primary-700 disabled:bg-primary-300 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            disabled={!isFormValid}
            type="submit"
          >
            Submit Request
          </button>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              className="flex-1 text-primary-600 hover:text-primary-700 font-medium py-2 px-4 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-0"
              onClick={(e) => {
                e.preventDefault();
                navigate("/");
              }}
              type="button"
            >
              Back to Sign In
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export { AuthRequestAccountCreation };
